import cn from "classnames";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useHasAdminRole } from "services/auth/useHasAdminRole";
import { useDatabasesNames } from "services/databases/useDatabasesNames";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";
import { useServiceAccounts } from "services/serviceAccounts/useServiceAccounts";

import {
  CreateServiceAccount,
  CreatedPayload,
} from "pages/configure/ServiceAccounts/ServiceAccountFormModal/ModalsWithUser/CreateServiceAccount";
import { createServiceAccountPrivilege } from "pages/configure/privileges";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import { useAccessManager } from "components/App/accessManager";
import { ButtonTemplate } from "components/Button/Button";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { useCopy } from "components/CopyProvider";
import FormRow from "components/FormRow";
import { CheckIcon, CopyIcon } from "components/Icons";
import { Modal } from "components/Modal/Modal";
import OutlinedSelect from "components/OutlinedSelect";
import ReadOnlyEditor from "components/ReadOnlyEditor";
import Tabs from "components/Tabs/Tabs";
import { TextInput } from "components/TextInput/TextInput";
import Tooltip from "components/Tooltip";

import {
  INSTALL_COMMANDS,
  SNIPPET_CONFIG,
  type UsageContext,
  type UsageSnippet,
  replacePlaceholders,
} from "./config/codeSnippets";
import { SDK_OPTIONS, Sdk } from "./config/config";

import styles from "./ConnectModal.module.scss";

const isLite = Boolean(process.env.REACT_APP_LITE);

type ConnectModalProps = {
  onClose: () => void;
  defaultDatabase?: string;
  defaultEngine?: string;
};

const CREATE_SERVICE_ACCOUNT_VALUE = "__create_service_account__";

const CopyControl = (props: { value?: string; testId: string }) => {
  const { value = "", testId } = props;
  const { copied, handleCopy } = useCopy();
  const { t } = useTranslation();

  if (!value) {
    return null;
  }

  return (
    <div
      className={styles.inputCopyIcon}
      onClick={() => {
        handleCopy(value);
      }}
      data-testid={testId}
    >
      <Tooltip
        title={t("connect_modal.copied")}
        open={!!copied}
      >
        <span className={styles.icon}>
          {copied ? (
            <CheckIcon data-testid={`${testId}-copied-icon`} />
          ) : (
            <CopyIcon />
          )}
        </span>
      </Tooltip>
    </div>
  );
};

const ConnectModal = (props: ConnectModalProps) => {
  const { onClose, defaultDatabase, defaultEngine } = props;
  const { t } = useTranslation();

  const { data: serviceAccounts = [] } = useServiceAccounts();
  const { data: engines = [] } = useWorkspaceEngines();
  const databases = useDatabasesNames();
  const { getAccount } = useCurrentAccount();
  const currentAccount = getAccount();
  const { isAllowedTo } = useAccessManager();
  const isOrganizationAdmin = useHasAdminRole();

  const [selectedServiceAccountName, setSelectedServiceAccountName] =
    useState<string>(() => {
      if (serviceAccounts.length > 0) {
        return serviceAccounts[0].serviceAccountName;
      }
      return "";
    });

  const [selectedEngineName, setSelectedEngineName] = useState<string>(() => {
    if (defaultEngine !== undefined && defaultEngine !== "system") {
      const isValidDefault = engines.some(e => e.engineName === defaultEngine);
      return isValidDefault ? defaultEngine : "";
    }
    return engines.length > 0 ? engines[0].engineName : "";
  });

  const [selectedDatabaseName, setSelectedDatabaseName] = useState<string>(
    () => {
      if (defaultDatabase !== undefined) {
        const isValidDefault =
          databases?.some(d => d.catalogName === defaultDatabase) ?? false;
        return isValidDefault ? defaultDatabase : "";
      }
      return databases && databases.length > 0 ? databases[0].catalogName : "";
    }
  );
  const [selectedSdk, setSelectedSdk] = useState<Sdk>(SDK_OPTIONS[0]?.sdk);
  const [serviceAccountSecrets, setServiceAccountSecrets] = useState<
    Record<string, string>
  >({});

  const [isCreateServiceAccountOpen, setIsCreateServiceAccountOpen] =
    useState(false);

  const selectedServiceAccount = serviceAccounts.find(
    account => account.serviceAccountName === selectedServiceAccountName
  );

  const selectedSdkOption = SDK_OPTIONS.find(
    option => option.sdk === selectedSdk
  );

  const serviceAccountId = selectedServiceAccount?.serviceAccountId ?? "";
  const serviceAccountSecret = serviceAccountSecrets[serviceAccountId];

  const usageContext: UsageContext = {
    accountName: currentAccount.accountName,
    databaseName: selectedDatabaseName,
    engineName: selectedEngineName,
    serviceAccountId,
    serviceAccountSecret,
  };

  const installCommand: string = INSTALL_COMMANDS[selectedSdk];
  const exportServiceAccountSecretCommand = `export SERVICE_ACCOUNT_SECRET="${serviceAccountSecret || "your_service_account_secret"}"`;
  const exportServiceAccountIdCommand = `export SERVICE_ACCOUNT_ID="${serviceAccountId}"`;

  const usageSnippet: UsageSnippet = {
    code: replacePlaceholders(
      SNIPPET_CONFIG[selectedSdk].template,
      usageContext
    ),
    language: SNIPPET_CONFIG[selectedSdk].language,
  };

  const handleServiceAccountCreated = (payload: CreatedPayload) => {
    const { secret } = payload;

    if (secret) {
      setServiceAccountSecrets(prev => ({
        ...prev,
        [payload.serviceAccountId]: secret,
      }));
    }
    setSelectedServiceAccountName(payload.serviceAccountName);
  };

  const renderServiceAccountItems = () => {
    const canCreateServiceAccount =
      isOrganizationAdmin ||
      isAllowedTo(
        createServiceAccountPrivilege.objectType,
        createServiceAccountPrivilege.objectName,
        createServiceAccountPrivilege.privilegeType
      );

    const items = [];

    if (canCreateServiceAccount) {
      items.push(
        <ContextMenuItem
          key="create-service-account"
          value={CREATE_SERVICE_ACCOUNT_VALUE}
          text={t("connect_modal.service_account.create")}
          checkedIconPlaceholder
          skipFilter
          testId="create-service-account-item"
        />,
        <ContextMenuDivider key="divider" />
      );
    }

    items.push(
      ...serviceAccounts.map(account => (
        <ContextMenuItem
          key={account.serviceAccountName}
          value={account.serviceAccountName}
          text={account.serviceAccountName}
          checkedIconPlaceholder
          checked={account.serviceAccountName === selectedServiceAccountName}
          testId={`service-account-item-${account.serviceAccountName}`}
        />
      ))
    );

    if (!serviceAccounts.length) {
      items.push(
        <ContextMenuItem
          key="no-service-accounts"
          value="no-service-accounts"
          text={t("connect_modal.service_account.no_accounts")}
          disabled
          skipFilter
        />
      );
    }

    return items;
  };

  const engineItems = engines?.map(engine => (
    <ContextMenuItem
      key={engine.engineName}
      value={engine.engineName}
      text={engine.engineName}
      checkedIconPlaceholder
      checked={engine.engineName === selectedEngineName}
      testId={`engine-item-${engine.engineName}`}
    />
  ));

  const databaseItems = databases?.map(database => (
    <ContextMenuItem
      key={database.catalogName}
      value={database.catalogName}
      testId={`database-item-${database.catalogName}`}
      text={database.catalogName}
      checkedIconPlaceholder
      checked={database.catalogName === selectedDatabaseName}
    />
  ));

  return (
    <>
      <Modal
        onClose={onClose}
        title={t("connect_modal.title")}
        hideCancel
        maxWidth="lg"
        hideFooter
        primaryButton={{
          text: t("connect_modal.close"),
          template: ButtonTemplate.Ghost,
          onSubmit: event => {
            event.preventDefault();
            onClose();
          },
        }}
      >
        <div className={styles.root}>
          <div className={styles.leftColumn}>
            {!isLite && (
              <>
                <div className={styles.section}>
                  <div className={styles.title}>
                    {t("connect_modal.authentication_section_title")}
                  </div>
                  <div className={styles.content}>
                    <FormRow
                      label={t("connect_modal.service_account.label")}
                      classes={{
                        inputContainer: styles.inputContainer,
                      }}
                      input={
                        <OutlinedSelect
                          testId="connect-service-account-select"
                          className={styles.dropdown}
                          allowUncheck={false}
                          controlledValue={
                            selectedServiceAccountName
                              ? [selectedServiceAccountName]
                              : []
                          }
                          onSelect={items => {
                            if (!items.length) {
                              return;
                            }
                            const [value] = items;
                            if (value === CREATE_SERVICE_ACCOUNT_VALUE) {
                              setIsCreateServiceAccountOpen(true);
                              return;
                            }
                            setSelectedServiceAccountName(value);
                          }}
                          searchOptions={{
                            searchPlaceholder: t(
                              "connect_modal.service_account.search_placeholder"
                            ),
                            hideSearch: serviceAccounts.length <= 5,
                          }}
                        >
                          {renderServiceAccountItems()}
                        </OutlinedSelect>
                      }
                    />
                  </div>
                </div>

                {!!selectedServiceAccountName && (
                  <>
                    <div className={styles.section}>
                      <div className={styles.header}>
                        {t("connect_modal.service_account.id")}
                      </div>
                      <div className={styles.content}>
                        <TextInput
                          value={serviceAccountId}
                          disabled
                          readOnly
                          inputRootClassName={styles.input}
                          testId="service-account-id"
                          endAdornment={
                            <CopyControl
                              value={serviceAccountId}
                              testId="copy-service-account-id"
                            />
                          }
                        />
                      </div>
                    </div>

                    <div className={styles.section}>
                      <div className={styles.header}>
                        {t("connect_modal.service_account.secret")}
                      </div>
                      <div className={styles.content}>
                        {serviceAccountSecret ? (
                          <TextInput
                            value={serviceAccountSecret}
                            disabled={true}
                            readOnly
                            inputRootClassName={styles.input}
                            testId="service-account-secret"
                            endAdornment={
                              <CopyControl
                                value={serviceAccountSecret}
                                testId="copy-service-account-secret"
                              />
                            }
                          />
                        ) : (
                          <div
                            className={styles.info}
                            data-testid="service-account-secret"
                          >
                            {t(
                              "connect_modal.service_account.secret_placeholder"
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className={styles.divider} />
              </>
            )}

            <div className={styles.section}>
              <div className={styles.title}>
                {t("connect_modal.connectivity_section_title")}
              </div>
              <div className={styles.content}>
                <FormRow
                  label={t("connect_modal.engine.label")}
                  classes={{
                    inputContainer: styles.inputContainer,
                    row: styles.row,
                  }}
                  input={
                    <OutlinedSelect
                      testId="connect-engine-select"
                      className={styles.dropdown}
                      allowUncheck={false}
                      controlledValue={
                        selectedEngineName ? [selectedEngineName] : []
                      }
                      onSelect={items => {
                        if (!items.length) {
                          return;
                        }
                        const [value] = items;
                        setSelectedEngineName(value);
                      }}
                      searchOptions={{
                        searchPlaceholder: t(
                          "connect_modal.engine.search_placeholder"
                        ),
                      }}
                    >
                      {engineItems}
                    </OutlinedSelect>
                  }
                />

                <FormRow
                  label={t("connect_modal.database.label")}
                  classes={{
                    inputContainer: styles.inputContainer,
                    row: styles.row,
                  }}
                  input={
                    <OutlinedSelect
                      testId="connect-database-select"
                      className={styles.dropdown}
                      allowUncheck={false}
                      controlledValue={
                        selectedDatabaseName ? [selectedDatabaseName] : []
                      }
                      onSelect={items => {
                        if (!items.length) {
                          return;
                        }
                        const [value] = items;
                        setSelectedDatabaseName(value);
                      }}
                      searchOptions={{
                        searchPlaceholder: t(
                          "connect_modal.database.search_placeholder"
                        ),
                      }}
                    >
                      {databaseItems}
                    </OutlinedSelect>
                  }
                />
              </div>
            </div>
          </div>

          <div className={styles.rightColumn}>
            <div
              className={styles.tabsContainer}
              data-testid="connect-sdk-select"
            >
              <Tabs
                items={SDK_OPTIONS.map(option => ({
                  key: option.sdk,
                  title: option.label,
                  render: ({ className, key, isActive }) => (
                    <div
                      className={cn(className, styles.tab, {
                        [styles.active]: isActive,
                      })}
                      onClick={() => setSelectedSdk(key as Sdk)}
                      data-testid={`sdk-tab-${option.sdk}`}
                    >
                      {option.label}
                    </div>
                  ),
                }))}
                activeKey={selectedSdk}
              />
            </div>

            <div className={cn(styles.section)}>
              <div className={styles.header}>{t("connect_modal.install")}</div>
              <div className={styles.content}>
                <div className={styles.row}>
                  <TextInput
                    value={exportServiceAccountSecretCommand}
                    disabled
                    readOnly
                    inputRootClassName={cn(styles.input, styles.installCommand)}
                    testId="export-secret-variable"
                    endAdornment={
                      <CopyControl
                        value={exportServiceAccountSecretCommand}
                        testId="copy-secret-setup-command"
                      />
                    }
                  />
                </div>
                {!!serviceAccountId && (
                  <div className={styles.row}>
                    <TextInput
                      value={exportServiceAccountIdCommand}
                      disabled
                      readOnly
                      inputRootClassName={cn(
                        styles.input,
                        styles.installCommand
                      )}
                      testId="export-variable-id"
                      endAdornment={
                        <CopyControl
                          value={exportServiceAccountIdCommand}
                          testId="copy-setup-id-command"
                        />
                      }
                    />
                  </div>
                )}
                <TextInput
                  value={installCommand}
                  disabled
                  readOnly
                  inputRootClassName={cn(styles.input, styles.installCommand)}
                  testId="install-command"
                  endAdornment={
                    <CopyControl
                      value={installCommand}
                      testId="copy-install-command"
                    />
                  }
                />
              </div>
            </div>

            <div className={cn(styles.section, styles.separated)}>
              <div className={styles.header}>{t("connect_modal.usage")}</div>
              <div className={styles.content}>
                <div
                  className={styles.usageSnippet}
                  data-testid="usage-snippet"
                >
                  <ReadOnlyEditor
                    value={usageSnippet.code}
                    language={usageSnippet.language}
                  />
                  <div className={styles.copyControl}>
                    <CopyControl
                      value={usageSnippet.code}
                      testId="copy-usage-snippet"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.documentationNote}>
              {selectedSdkOption?.docsUrl && (
                <Trans
                  i18nKey="connect_modal.documentation.text"
                  values={{ sdk: selectedSdkOption.label }}
                  components={{
                    docsLink: (
                      <a
                        href={selectedSdkOption.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="documentation"
                        data-testid="connect-doc-link"
                      >
                        {/* Content will be injected by Trans component */}
                      </a>
                    ),
                  }}
                />
              )}
              {selectedSdkOption?.examplesUrl && (
                <>
                  {selectedSdkOption?.docsUrl && " "}
                  <Trans
                    i18nKey="connect_modal.examples.text"
                    components={{
                      examplesLink: (
                        <a
                          href={selectedSdkOption.examplesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="examples"
                          data-testid="connect-examples-link"
                        >
                          {/* Content will be injected by Trans component */}
                        </a>
                      ),
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {!isLite && isCreateServiceAccountOpen && (
        <CreateServiceAccount
          onClose={() => setIsCreateServiceAccountOpen(false)}
          allowedAccountNames={[currentAccount.accountName]}
          onCompleted={handleServiceAccountCreated}
        />
      )}
    </>
  );
};

export default ConnectModal;
